import { useState, useEffect } from 'react';
import { useLogout, useUser, useUpdate } from "../../../hooks/useAuth"
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Card from '../../ui/Card';
import Input from '../../ui/Input';
import Button from '../../ui/Button';
import Tabs from '../../ui/Tabs';
import Avatar from '../../ui/Avatar';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

function AccountSettings() {
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const { REACT_APP_IMG_URL } = process.env
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useUser()
  const logout = useLogout()
  const updateMutation = useUpdate()
  const { isSuccess: isUserSuccess, data: userResponse, isError: isUserError } = user

  const handlePhoneChange = (value, country) => {
    const fullPhone = value; 
    const dialCode = country?.dialCode || ''; 
    const phoneOnly = fullPhone.startsWith(dialCode)
      ? fullPhone.slice(dialCode.length)
      : fullPhone;
    setProfileData((prev) => ({
      ...prev,
      phone_number: phoneOnly,
      country_code: `+${country.dialCode}`
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileData((prev) => ({
        ...prev,
        profile_pic: file
      }));
      const imageURL = URL.createObjectURL(file);
      setImagePreview(imageURL);
    }
  };

  const handleLogout = async () => {
    logout.mutate(null, {
      onSuccess: () => {
        navigate('/login');
        toast.success('Logged out successfully');
      },
      onError: () => {
        navigate('/login');
        toast.error('Session expired, logged out');
      }
    })
  };

  useEffect(() => {
    if (isUserError) {
      handleLogout()
    }
    if (isUserSuccess) {
      let data = userResponse?.data?.data
      setImagePreview(REACT_APP_IMG_URL+"/"+data?.profile_pic)
      setProfileData((prev) => ({
        ...prev,
        first_name: data?.first_name,
        last_name: data?.last_name,
        email: data?.email,
        phone_number: data?.phone_number,
        country_code: data?.country_code,
        greet_msg: data?.greet_msg,
        bio: data?.bio
      }))
    }
  }, [userResponse, isUserError])

  // Security state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'security', label: 'Security' },
  ];

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    updateMutation.mutate(profileData, {
      onSuccess: () => {
        toast.success('Profile updated successfully!');
        queryClient.invalidateQueries({ queryKey: ['onboarding_screens'] });
      },
      onError: (error) => {
        const message = error.response?.data?.message
        toast.error(message)
      } 
    })
  };

  // ******** Security handlers ********
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    // Validate password match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    // setIsLoading(true);
    // try {
    //   const result = await changePassword(
    //     passwordData.currentPassword,
    //     passwordData.newPassword
    //   );
    //   if (result.success) {
    //     setSuccessMessage('Password changed successfully!');
    //     setPasswordData({
    //       currentPassword: '',
    //       newPassword: '',
    //       confirmPassword: '',
    //     });
    //   } else {
    //     setErrorMessage(result.error || 'Failed to change password');
    //   }
    // } catch (error) {
    //   setErrorMessage('An unexpected error occurred');
    // } finally {
    //   setIsLoading(false);
    // }
  };

  return (
    <div className='mb-20'>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
        <p className="text-gray-500 mt-1">
          Manage your account details and security settings
        </p>
      </div>
      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      <div className="mt-6">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <Card>
            <form onSubmit={handleProfileSubmit}>
              <div className="mb-6 flex items-center">
                <Avatar
                  src={imagePreview}
                  alt={"Profile Pic"}
                  size="xl"
                />
                <div className="ml-5">
                  <Button variant="secondary" size="sm" className="mr-2 h-8 w-[130px]">
                    Change Avatar
                  </Button>
                  <input
                    type='file'
                    className='h-8 w-[130px] ml-[-140px]'
                    style={{ position: 'absolute', opacity: 0, cursor: 'pointer' }}
                    accept='image/*'
                    onChange={handleImageChange}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Input
                  label="First Name"
                  name="first_name"
                  value={`${profileData?.first_name??""}`}
                  onChange={handleProfileChange}
                  required
                />
                <Input
                  label="Last Name"
                  name="last_name"
                  value={`${profileData?.last_name??""}`}
                  onChange={handleProfileChange}
                />
                <Input
                  label="Email Address"
                  type="email"
                  value={profileData?.email??""}
                  disabled
                />
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Phone Number</label>
                  <PhoneInput
                    country={'us'} // default country
                    value={`${(profileData?.country_code || '').replace('+', '')}${profileData?.phone_number || ''}`}  
                    onChange={handlePhoneChange}
                    inputProps={{
                      name: 'phone',
                      required: true,
                      autoFocus: false
                    }}
                    inputClass="!w-full !p-6 !rounded-md !border !border-gray-300"
                    containerClass="!w-full"
                    countryCodeEditable={false}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="bio"
                    className="block text-sm font-medium text-gray-700 mb-1">
                    Bio
                  </label>
                  <textarea
                    id="bio"
                    name="bio"
                    rows={4}
                    value={profileData?.bio??""}
                    onChange={handleProfileChange}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                    placeholder="Tell us about yourself..."
                  />
                </div>
              </div>
              <div className="mt-6">
                <Button type="submit" disabled={updateMutation.isLoading}>
                  {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </Card>
        )}
        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            {/* Change Password */}
            <Card title="Change Password">
              <form onSubmit={handlePasswordSubmit}>
                <div className="space-y-4">
                  <Input
                    label="Current Password"
                    type="password"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                  <Input
                    label="New Password"
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                  <Input
                    label="Confirm New Password"
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
                <div className="mt-6">
                  <Button type="submit" disabled={updateMutation.isLoading}>
                    {updateMutation.isLoading ? 'Updating...' : 'Update Password'}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
export default AccountSettings;